class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());
    // Delegacao no custom element: o #CartDrawer-Overlay e substituido nos
    // re-renders via Sections, mas <cart-drawer> permanece. Assim o clique no
    // overlay continua funcionando inclusive em renderContents(..., false).
    this.addEventListener("click", (evt) => {
      if (
        evt.target &&
        typeof evt.target.closest === "function" &&
        evt.target.closest("#CartDrawer-Overlay")
      ) {
        this.close();
      }
    });
    this.setHeaderCartIconAccessibility();
    this.checkUrlForOpenCart();
    this.initSwipeToClose();
    this.initGiftCardCheckoutGuard();
    this.initGiftCardQuantityLimit();
    this.initGiftOrderCheckboxes();
    this.initGiftWarning();

    // Reaplica bloqueio de quantidade para itens com 🎁 em qualquer atualização do carrinho
    if (!this.__giftIconQtyLockWired) {
      this.__giftIconQtyLockWired = true;

      const rerunGiftLock = () => {
        // aguarda o DOM do drawer ser atualizado
        setTimeout(() => this.lockGiftIconLineItemQuantityControls(), 0);
      };

      // Eventos internos/custom que disparamos no tema
      ["cart:updated", "cart:change", "cart:refresh", "cart:requestComplete", "quantity:change"].forEach((evt) =>
        document.addEventListener(evt, rerunGiftLock),
      );

      // Integração com PUB_SUB do Shopify (Dawn)
      if (typeof subscribe === "function" && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, rerunGiftLock);
      }

      // Fallback: observa mudanças no DOM do drawer (remoção/adição de itens, re-render via Sections)
      const cartRoot = document.getElementById("CartDrawer") || this;
      if (cartRoot && "MutationObserver" in window) {
        const mo = new MutationObserver(() => rerunGiftLock());
        mo.observe(cartRoot, { subtree: true, childList: true });
        this.__giftIconQtyLockObserver = mo;
      }

      // Execução inicial
      rerunGiftLock();
    }

    // Listen for event when product is added to cart by Instagram Section (Stamped)
    // stamped:widget:instagram-feed:added-to-cart
    document.addEventListener("stamped:widget:instagram-feed:added-to-cart", (e) => {
      const currentHref = window.location.href.split("?")[0];
      const currentParams = window.location.href.split("?")[1];
      const redirectUrl = `${currentHref}${currentParams ? `?${currentParams}&` : "?"}${
        !currentParams || !currentParams.includes("openCart") ? "openCart=true" : ""
      }`;

      window.location.href = redirectUrl;
    });
  }

  initGiftCardCheckoutGuard() {
    this.addEventListener(
      "submit",
      (event) => {
        const form = event.target;
        if (!form || form.id !== "CartDrawer-Form") return;

        const warning = this.querySelector(".cart-giftcard-warning");
        if (!warning) return;

        // Make the warning much more evident
        warning.classList.add("cart-giftcard-warning--highlight");

        try {
          warning.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        } catch (e) {
          // Fallback for older browsers
          warning.scrollIntoView();
        }

        // Remove the highlight class after a short delay so it can animate again if the user clicks checkout repeatedly
        setTimeout(() => {
          warning.classList.remove("cart-giftcard-warning--highlight");
        }, 800);
      },
      false,
    );
  }

  initGiftWarning() {
    let giftWarningEligible = false;

    const isGiftOrderSelected = () =>
      Array.from(document.querySelectorAll(".js-gift-order-checkbox")).some((checkbox) => checkbox.checked);

    const updateGiftWarningVisibility = () => {
      const element = document.querySelector("[data-gift-warn-threshold]");
      if (!element) return;

      const shouldShow = giftWarningEligible && isGiftOrderSelected();
      element.hidden = !shouldShow;
    };

    const evaluateGiftWarning = (cart) => {
      const element = document.querySelector("[data-gift-warn-threshold]");
      if (!element) return;

      const threshold = parseInt(element.dataset.giftWarnThreshold, 10) || 0;
      const warningIds = element.dataset.giftWarnProducts
        ? element.dataset.giftWarnProducts.split(",").map(Number).filter(Boolean)
        : [];
      let eligible = threshold > 0 && cart.total_price >= threshold;

      if (!eligible && warningIds.length > 0) {
        const cartIds = (cart.items || []).map((item) => item.product_id);
        eligible = warningIds.some((id) => cartIds.indexOf(id) !== -1);
      }

      giftWarningEligible = eligible;
      updateGiftWarningVisibility();
    };

    const fetchAndEvaluate = () => {
      fetch("/cart.js")
        .then((response) => response.json())
        .then(evaluateGiftWarning)
        .catch(() => {});
    };

    const cartUpdateEvent = window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate;
    if (typeof subscribe === "function" && cartUpdateEvent) {
      subscribe(cartUpdateEvent, () => {
        setTimeout(fetchAndEvaluate, 0);
      });
    }

    document.addEventListener("gift-order-checkbox:changed", (event) => {
      if (event.detail.checked && event.detail.cart) evaluateGiftWarning(event.detail.cart);
      else updateGiftWarningVisibility();
    });

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fetchAndEvaluate, { once: true });
    } else {
      fetchAndEvaluate();
    }
  }

  initGiftOrderCheckboxes() {
    if (window.__giftOrderCheckboxInit) return;
    window.__giftOrderCheckboxInit = true;

    document.addEventListener("change", (event) => {
      if (!event.target.classList.contains("js-gift-order-checkbox")) return;

      const isChecked = event.target.checked;
      document.querySelectorAll(".js-gift-order-checkbox").forEach((checkbox) => {
        checkbox.checked = isChecked;
      });

      fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          attributes: {
            esse_pedido_um_presente: isChecked ? "true" : "",
          },
        }),
      })
        .then((response) => response.json())
        .then((cart) => {
          document.dispatchEvent(
            new CustomEvent("gift-order-checkbox:changed", {
              detail: { checked: isChecked, cart },
            }),
          );
        })
        .catch(() => {
          document.dispatchEvent(
            new CustomEvent("gift-order-checkbox:changed", {
              detail: { checked: isChecked, cart: null },
            }),
          );
        });
    });

    document.addEventListener("submit", (event) => {
      const form = event.target;
      if (form.id !== "cart" && form.id !== "CartDrawer-Form") return;

      const giftCheckbox = document.querySelector(".js-gift-order-checkbox");
      if (!giftCheckbox) return;

      const existingInput = form.querySelector('input[data-gift-order-attribute="true"]');
      if (existingInput) existingInput.remove();

      const attributeInput = document.createElement("input");
      attributeInput.type = "hidden";
      attributeInput.name = "attributes[esse_pedido_um_presente]";
      attributeInput.value = giftCheckbox.checked ? "true" : "";
      attributeInput.setAttribute("data-gift-order-attribute", "true");
      form.appendChild(attributeInput);
    });
  }

  initGiftCardQuantityLimit() {
    const MAX_GIFT_CARDS = 5;
    const root = this;

    function showGiftCardLimitError() {
      const errorContainer = root.querySelector("#CartDrawer-CartErrors");
      if (!errorContainer) return;

      errorContainer.innerHTML = `<div class="cart-giftcard-warning cart-giftcard-warning--highlight">Você já adicionou o número máximo permitido de 5 Gift Cards neste pedido.</div>`;
      errorContainer.style.display = "block";

      try {
        errorContainer.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      } catch (e) {
        errorContainer.scrollIntoView();
      }

      // Remove o destaque depois de um tempo para poder aplicar novamente em futuros erros
      if (root._giftCardLimitHighlightTimeout) {
        clearTimeout(root._giftCardLimitHighlightTimeout);
      }
      root._giftCardLimitHighlightTimeout = setTimeout(() => {
        const warningEl = errorContainer.querySelector(".cart-giftcard-warning");
        if (warningEl) {
          warningEl.classList.remove("cart-giftcard-warning--highlight");
        }
      }, 800);

      // Esconde o erro depois de alguns segundos
      if (root._giftCardLimitHideTimeout) {
        clearTimeout(root._giftCardLimitHideTimeout);
      }
      root._giftCardLimitHideTimeout = setTimeout(() => {
        errorContainer.style.display = "none";
      }, 5000);
    }

    function isGiftCardItem(cartItemEl) {
      if (!cartItemEl) return false;
      const nameEl = cartItemEl.querySelector(".cart-item__name");
      if (!nameEl) return false;
      const title = (nameEl.textContent || "").toLowerCase();
      return title.includes("gift card") || title.includes("cartão presente");
    }

    function countGiftCards() {
      let total = 0;
      root.querySelectorAll(".cart-item").forEach((itemEl) => {
        if (!isGiftCardItem(itemEl)) return;
        const input = itemEl.querySelector(".quantity__input");
        const qty = parseInt(input && input.value, 10);
        if (!Number.isNaN(qty) && qty > 0) {
          total += qty;
        }
      });
      return total;
    }

    // Guarda o valor anterior quando o usuário foca no input de quantidade
    this.addEventListener(
      "focusin",
      (event) => {
        const input = event.target.closest(".quantity__input");
        if (!input) return;
        const cartItemEl = input.closest(".cart-item");
        if (!cartItemEl || !isGiftCardItem(cartItemEl)) return;
        input.dataset.previousValue = input.value;
      },
      true,
    );

    // Bloqueia incremento pelo botão (+) quando já atingiu o limite
    this.addEventListener(
      "click",
      (event) => {
        const btn = event.target.closest(".quantity__button");
        if (!btn) return;

        const isPlus = btn.name === "plus";
        if (!isPlus) return;

        const cartItemEl = btn.closest(".cart-item");
        if (!cartItemEl || !isGiftCardItem(cartItemEl)) return;

        const currentTotal = countGiftCards();
        const input = cartItemEl.querySelector(".quantity__input");
        const currentItemQty = parseInt(input && input.value, 10) || 0;
        const newTotal = currentTotal + 1;

        // Se este clique vai ultrapassar o limite, bloqueia o incremento
        if (newTotal > MAX_GIFT_CARDS) {
          event.preventDefault();
          event.stopPropagation();
          showGiftCardLimitError();
          return;
        }

        // Se este clique vai levar exatamente ao limite (ex.: de 4 para 5),
        // permitimos o incremento, mas já exibimos a mensagem
        if (newTotal === MAX_GIFT_CARDS) {
          showGiftCardLimitError();
        }
      },
      true,
    );

    // Bloqueia mudanças manuais no input de quantidade que levem a mais de 5 Gift Cards no total
    this.addEventListener(
      "change",
      (event) => {
        const input = event.target.closest(".quantity__input");
        if (!input) return;
        const cartItemEl = input.closest(".cart-item");
        if (!cartItemEl || !isGiftCardItem(cartItemEl)) return;

        const total = countGiftCards();

        // Se o total ultrapassar o limite, bloqueia e restaura o valor anterior
        if (total > MAX_GIFT_CARDS) {
          event.preventDefault();
          event.stopPropagation();
          showGiftCardLimitError();

          if (input.dataset.previousValue !== undefined) {
            input.value = input.dataset.previousValue;
          } else {
            input.value = "1";
          }
          return;
        }

        // Se o total for exatamente o limite, apenas exibe a mensagem
        if (total === MAX_GIFT_CARDS) {
          showGiftCardLimitError();
        }
      },
      true,
    );
  }

  // Quando o item tiver o ícone 🎁 no nome, não permitir manipulação de quantidade
  lockGiftIconLineItemQuantityControls() {
    const cartRoot = document.getElementById("CartDrawer") || this;
    if (!cartRoot) return;

    const cartItems = cartRoot.querySelectorAll(".cart-item");
    if (!cartItems || !cartItems.length) return;

    cartItems.forEach((itemEl) => {
      const nameEl = itemEl.querySelector(".cart-item__name");
      const title = nameEl && nameEl.textContent ? nameEl.textContent : "";

      if (!title.includes("🎁")) return;

      // Desativa botões de + e - (mantém no DOM, mas não clicáveis)
      itemEl.querySelectorAll(".quantity__button").forEach((btn) => {
        btn.setAttribute("disabled", "disabled");
        btn.setAttribute("aria-disabled", "true");
        btn.classList.add("quantity__button--disabled");

        // Garante que nenhum handler de clique seja executado
        btn.addEventListener(
          "click",
          (e) => {
            e.preventDefault();
            e.stopPropagation();
          },
          true,
        );
      });

      // Bloqueia edição direta no input, caso exista
      const input = itemEl.querySelector(".quantity__input");
      if (input) {
        input.setAttribute("readonly", "readonly");
        input.setAttribute("aria-readonly", "true");
        input.classList.add("quantity__input--locked");

        // Evita eventos de alteração via teclado/scroll
        input.addEventListener(
          "keydown",
          (e) => {
            e.preventDefault();
            e.stopPropagation();
          },
          true,
        );

        input.addEventListener(
          "wheel",
          (e) => {
            e.preventDefault();
            e.stopPropagation();
          },
          { passive: false, capture: true },
        );
      }
    });
  }

  initSwipeToClose() {
    const drawerEl = document.getElementById("CartDrawer") || this;
    if (!drawerEl || !("ontouchstart" in window)) return;

    let startX = 0;
    let startY = 0;
    let currentX = 0;
    let currentY = 0;
    const threshold = 60; // px

    const onTouchStart = (e) => {
      if (!this.classList.contains("active")) return;
      const touch = e.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      currentX = startX;
      currentY = startY;
    };

    const onTouchMove = (e) => {
      if (!this.classList.contains("active")) return;
      const touch = e.touches[0];
      currentX = touch.clientX;
      currentY = touch.clientY;
    };

    const onTouchEnd = () => {
      if (!this.classList.contains("active")) return;

      const dx = currentX - startX;
      const dy = currentY - startY;

      // horizontal swipe to the right with minimal vertical movement
      if (Math.abs(dx) > Math.abs(dy) && dx > threshold) {
        this.close();
      }

      startX = 0;
      startY = 0;
      currentX = 0;
      currentY = 0;
    };

    drawerEl.addEventListener("touchstart", onTouchStart, { passive: true });
    drawerEl.addEventListener("touchmove", onTouchMove, { passive: true });
    drawerEl.addEventListener("touchend", onTouchEnd);
  }

  checkUrlForOpenCart() {
    window.location.href.includes("openCart") ? this.open() : false;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    Array.from(document.getElementsByClassName("cart-item__details")).forEach((item) => {
      if (item.querySelector(".cart-item__name").textContent.includes("Gift Card")) {
        Array.from(item.querySelectorAll(".product-option")).forEach((option) => {
          if (option.querySelector("dt") && option.querySelector("dd")) {
            option.querySelector("dt").textContent === "Recipient email:"
              ? (option.querySelector("dt").textContent = "E-mail do presenteado:")
              : null;
            option.querySelector("dt").textContent === "Send on:"
              ? (option.querySelector("dt").textContent = "Data de envio:")
              : null;
            if (
              window.innerWidth < 768 &&
              (option.querySelector("dt").textContent === "Message:" ||
                option.querySelector("dt").textContent === "Mensagem:")
            ) {
              option.querySelector("dt").remove();
              option.querySelector("dd").remove();
            } else {
              option.querySelector("dt").textContent === "Message:"
                ? (option.querySelector("dt").textContent = "Mensagem:")
                : null;
            }
          }
        });
      }
    });
    this.lockGiftIconLineItemQuantityControls();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        let containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        if (!containerToTrapFocusOn) containerToTrapFocusOn = document.getElementById("CartDrawer");
        const focusElement = this.querySelector(".drawer__inner") || this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute("aria-controls", cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute("aria-expanded", !event.currentTarget.closest("details").hasAttribute("open"));
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  getCart(callback) {
    fetch("/cart.js", {
      credentials: "same-origin",
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Cache-Control": "max-age=5",
      },
    })
      .then((response) => response.json())
      .then((cart) => {
        callback(cart);
      })
      .catch((error) => console.error("Error:", error));
  }

  updateCart(shouldOpen = true) {
    const sections = this.getSectionsToRender().map((section) => section.id);
    const url = `/?sections=${sections.join(",")}`;

    return fetch(url, {
      credentials: "same-origin",
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((sections) => {
        const parsedState = {
          id: 0,
          sections,
        };

        this.renderContents(parsedState, shouldOpen);
        return parsedState;
      })
      .catch((error) => {
        console.error("Error:", error);
        throw error;
      });
  }

  syncEmptyState(parsedState) {
    const wasEmpty = this.classList.contains("is-empty");
    let isEmpty = null;

    if (parsedState && typeof parsedState.item_count === "number") {
      isEmpty = parsedState.item_count === 0;
    } else {
      const cartDrawerSection =
        parsedState &&
        parsedState.sections &&
        parsedState.sections["cart-drawer"];

      if (cartDrawerSection) {
        const sourceDrawer = new DOMParser()
          .parseFromString(cartDrawerSection, "text/html")
          .querySelector("cart-drawer");

        if (sourceDrawer) {
          isEmpty = sourceDrawer.classList.contains("is-empty");
        }
      }
    }

    if (isEmpty === null) {
      return {
        wasEmpty,
        isEmpty: wasEmpty,
        changed: false,
      };
    }

    this.classList.toggle("is-empty", isEmpty);

    return {
      wasEmpty,
      isEmpty,
      changed: wasEmpty !== isEmpty,
    };
  }

  renderContents(parsedState, shouldOpen = true) {
    // O custom element <cart-drawer> persiste entre os re-renders de Sections.
    // Sincroniza seu estado vazio com a resposta do Shopify antes de substituir
    // o conteudo interno, para que o CSS/markup de carrinho vazio seja exibido.
    const emptyState = this.syncEmptyState(parsedState);

    this.productId = parsedState.id;
    // console.log(`Product ID: ${this.productId}`);
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Após re-render do CartDrawer via Sections, reaplica o bloqueio de quantidade para itens com 🎁
    this.lockGiftIconLineItemQuantityControls();

    if (
      emptyState.changed &&
      emptyState.isEmpty &&
      this.classList.contains("active")
    ) {
      requestAnimationFrame(() => {
        const emptyContainer = this.querySelector(".drawer__inner-empty");
        if (!emptyContainer || typeof trapFocus !== "function") return;

        const focusTarget = emptyContainer.querySelector(
          "a, button, [href], [tabindex]:not([tabindex='-1'])",
        );

        if (focusTarget) {
          trapFocus(emptyContainer, focusTarget);
        }
      });
    }

    if (shouldOpen) {
      setTimeout(() => {
        this.open();
        this.lockGiftIconLineItemQuantityControls();
      });
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();
  }

  onCartUpdate() {
    const cartDrawer = document.querySelector("cart-drawer");

    if (!cartDrawer || typeof cartDrawer.updateCart !== "function") {
      console.error(
        "[cart-drawer-items] CartDrawer.updateCart indisponível no refresh do minicart.",
      );
      return;
    }

    cartDrawer.updateCart(false).catch((error) => {
      console.error(
        "[cart-drawer-items] Falha ao atualizar minicart:",
        error,
      );
    });
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}
customElements.define("cart-drawer-items", CartDrawerItems);

const ZIPCODE_RANGES = [
  { province: "SP", from: 1000000, to: 19999999 },
  { province: "RJ", from: 20000000, to: 28999999 },
  { province: "ES", from: 29000000, to: 29999999 },
  { province: "MG", from: 30000000, to: 39999999 },
  { province: "BA", from: 40000000, to: 48999999 },
  { province: "SE", from: 49000000, to: 49999999 },
  { province: "PE", from: 50000000, to: 56999999 },
  { province: "AL", from: 57000000, to: 57999999 },
  { province: "PB", from: 58000000, to: 58999999 },
  { province: "RN", from: 59000000, to: 59999999 },
  { province: "CE", from: 60000000, to: 63999999 },
  { province: "PI", from: 64000000, to: 64999999 },
  { province: "MA", from: 65000000, to: 65999999 },
  { province: "PA", from: 66000000, to: 68899999 },
  { province: "AP", from: 68900000, to: 68999999 },
  { province: "AM", from: 69000000, to: 69299999 },
  { province: "RR", from: 69300000, to: 69399999 },
  { province: "AM", from: 69400000, to: 69899999 },
  { province: "AC", from: 69900000, to: 69999999 },
  { province: "DF", from: 70000000, to: 72799999 },
  { province: "GO", from: 72800000, to: 72999999 },
  { province: "DF", from: 73000000, to: 73699999 },
  { province: "GO", from: 73700000, to: 76799999 },
  { province: "RO", from: 76800000, to: 76999999 },
  { province: "TO", from: 77000000, to: 77999999 },
  { province: "MT", from: 78000000, to: 78899999 },
  { province: "MS", from: 79000000, to: 79999999 },
  { province: "PR", from: 80000000, to: 87999999 },
  { province: "SC", from: 88000000, to: 89999999 },
  { province: "RS", from: 90000000, to: 99999999 },
];

class ShippingCalculator extends HTMLElement {
  constructor() {
    super();

    this.header = this.querySelector(".shipping-calculator__header");
    this.header.addEventListener("click", this.toggle.bind(this));

    this.zipcodeInput = this.querySelector("#shipping-calculator-zipcode");
    if (window.VMasker) window.VMasker(this.zipcodeInput).maskPattern("99999-999");

    this.calculateShippingBtn = this.querySelector(".shipping-calculator__calculate-button");
    this.calculateShippingBtn.addEventListener("click", this.prepareShippingRates.bind(this));

    this.ratesWrapper = this.querySelector(".shipping-calculator__result");
    this.showAllRatesBtn = this.querySelector(".rates__show-all-button");
    this.showAllRatesBtn.addEventListener("click", this.showAllRates.bind(this));

    // Check if zip is saved in session storage
    const savedZip = sessionStorage.getItem("insider:cart:shippingZip") || undefined;
    if (savedZip) {
      sessionStorage.setItem("insider:checkout:shippingZip", savedZip);

      this.zipcodeInput.value = savedZip;
      this.prepareShippingRates({ preventDefault: () => {} });
      this.toggle();
    }
    // Preorder flag
    this.hasPreorderItems = false;
  }

  toggle() {
    this.classList.toggle("shipping-calculator--open");
  }

  prepareShippingRates(e) {
    e.preventDefault();

    const country = "Brazil";
    const zip = this.zipcodeInput.value;
    // Save zip in session storage
    sessionStorage.setItem("insider:cart:shippingZip", zip);
    sessionStorage.setItem("insider:checkout:shippingZip", zip);

    const province = this.provinceByZip(zip);
    const query = `shipping_address[zip]=${zip}&shipping_address[country]=${country}&shipping_address[province]=${province}`;
    const url = `/cart/prepare_shipping_rates.json?${query}`;

    fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        this.calculateShipping(query);
      })
      .catch((error) => {
        console.error(error);
      });
  }

  calculateShipping(query) {
    const url = `/cart/async_shipping_rates.json?${query}`;

    fetch(url, {
      method: "GET",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data) {
          this.clearRates();
          this.checkPreorderInCart()
            .then((hasPreorder) => {
              this.hasPreorderItems = hasPreorder;
              this.querySelector(".shipping-calculator__result__rates").innerHTML = this.getRatesHTML(
                data.shipping_rates,
              );
              this.ratesWrapper.style.display = "block";
            })
            .catch((error) => {
              console.error("Error checking preorder in cart:", error);
              this.hasPreorderItems = false;
              this.querySelector(".shipping-calculator__result__rates").innerHTML = this.getRatesHTML(
                data.shipping_rates,
              );
              this.ratesWrapper.style.display = "block";
            });
        } else {
          setTimeout(() => {
            this.calculateShipping(query);
          }, 500);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }

  async checkPreorderInCart() {
    try {
      const res = await fetch("/cart.js", { credentials: "same-origin", headers: { "Cache-Control": "max-age=5" } });
      const cart = await res.json();

      const isPreorderItem = (item) => Boolean(item?.selling_plan_allocation || item?.selling_plan);

      return Array.isArray(cart.items) && cart.items.some(isPreorderItem);
    } catch (e) {
      console.error("checkPreorderInCart error:", e);
      return false;
    }
  }

  clearRates() {
    this.ratesWrapper.classList.remove("show-all-rates");
    this.showAllRatesBtn.classList.remove("hidden");
    this.querySelector(".shipping-calculator__result__rates").innerHTML = "";
  }

  getRatesHTML(rates) {
    return rates
      .map((rate) => {
        let deliveryDate = null;
        let deliveryDay = null;
        let deliveryMonth = null;
        if (rate.delivery_range && rate.delivery_range.length > 0) {
          deliveryDate = rate.delivery_range.length > 1 ? rate.delivery_range[1] : rate.delivery_range[0];
          deliveryDay = deliveryDate.split("-")[2];
          deliveryMonth = deliveryDate.split("-")[1];
        }

        const priceCents = parseFloat(rate.price) * 100;

        return `
          <div class="shipping-calculator__result-item">
            <p class="rate-name">${rate.name}${
              deliveryDate && !this.hasPreorderItems ? ` | Entrega até dia ${deliveryDay}/${deliveryMonth}` : ""
            }</p>
            <p class="rate-price">${priceCents > 0 ? this.formatMoney(priceCents) : "GRÁTIS"}</p>
          </div>
        `;
      })
      .join("");
  }

  formatMoney(cents) {
    const value = (cents / 100).toFixed(2).replace(".", ",");
    return `R$ ${value.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
  }

  provinceByZip(zip) {
    const zipNumber = parseInt(zip.replace("-", ""));
    const zipRange = ZIPCODE_RANGES.find((range) => range.from <= zipNumber && range.to >= zipNumber);
    return zipRange ? zipRange.province : "SP";
  }

  showAllRates(e) {
    e.preventDefault();
    this.ratesWrapper.classList.add("show-all-rates");
    this.showAllRatesBtn.classList.add("hidden");
  }
}
customElements.define("shipping-calculator", ShippingCalculator);

class PromotionCoupon extends HTMLElement {
  constructor() {
    super();
    this.header = this.querySelector(".promotion-coupon__header");
    this.header.addEventListener("click", this.toggle.bind(this));

    this.cartDrawer = document.querySelector("cart-drawer");

    this.inputPromotionCoupon = this.querySelector("#promotion-coupon-code");

    this.applyCouponBtn = this.querySelector(".promotion-coupon__calculate-button");
    this.applyCouponBtn.addEventListener("click", this.addPromotionCoupon.bind(this));

    this.clearButton = this.querySelector(".remove-promotion-coupon");
    this.clearButton.addEventListener("click", this.clearDiscount.bind(this));

    this.customerId = window?.__st?.cid ?? null;
  }

  connectedCallback() {
    this.getCart((cart) => {
      this.cart = cart;
      const appliedDiscountsToCart = (() => {
        const discounts = new Set([
          ...cart.cart_level_discount_applications.map((discount) => discount.title),
          ...this.getDiscountAppliedInProducts(cart),
        ]);
        return Array.from(discounts);
      })();
      const hasPromotionCouponInCache = this.setInformationPromotionCoupon();
      const hasSamePromotionCouponInCart = cart.cart_level_discount_applications.some(
        (discount) => discount.title === this.dataPromotionCoupon?.code,
      );
      const hasSamePromotionCouponInProducts = cart.items.some((item) =>
        item.discounts.some((discount) => discount.title === this.dataPromotionCoupon?.code),
      );
      const hasCouponFreeShipping = this.dataPromotionCoupon?.data?.discount?.target_type === "shipping_line";

      if (appliedDiscountsToCart.length && !appliedDiscountsToCart.includes(this.dataPromotionCoupon?.code)) {
        this.addPromotionCoupon(undefined, appliedDiscountsToCart[0]);
      } else if (hasPromotionCouponInCache && (hasSamePromotionCouponInCart || hasSamePromotionCouponInProducts)) {
        this.promotionCouponActive(cart);
      } else if (hasPromotionCouponInCache && hasCouponFreeShipping) {
        this.addPromotionCoupon(undefined, this.dataPromotionCoupon.code);
      } else if (hasPromotionCouponInCache) {
        this.addPromotionCoupon(undefined, this.dataPromotionCoupon.code);
      } else {
        this.clearDiscount();
      }
    });
  }

  setInformationPromotionCoupon() {
    const promotionCoupon = JSON.parse(sessionStorage.getItem("insider:cart:promotionCoupon")) || undefined;
    if (!promotionCoupon || !promotionCoupon.code) return false;
    this.dataPromotionCoupon = promotionCoupon;
    this.inputPromotionCoupon.value = this.dataPromotionCoupon.code;

    return true;
  }

  getDiscountAppliedInProducts(cart) {
    const discountTitles = new Set();
    cart.items.forEach((item) => {
      item.discounts.forEach((discount) => {
        if (
          discount.title &&
          !discount.title.includes("Desconto kit") &&
          !discount.title.includes("Kit discount") &&
          !discount.title.includes("%") &&
          !discount.title.includes("R$")
        ) {
          discountTitles.add(discount.title.trim());
        }
      });
    });
    return Array.from(discountTitles);
  }

  formatMoney(cents) {
    const value = (cents / 100).toFixed(2).replace(".", ",");
    return `R$ ${value.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
  }

  toggle() {
    this.classList.toggle("promotion-coupon--open");
  }

  promotionCouponActive(oldCart) {
    this.classList.add("promotion-coupon--open");
    this.classList.add("coupon-active");

    this.querySelector(".text-promotion-code").innerHTML = this.dataPromotionCoupon.code;
    const discountData = this.dataPromotionCoupon.data.discount;

    if (discountData) {
      if (discountData.target_type === "shipping_line") {
        this.classList.add("coupon-active-free-shipping");
      }

      if (discountData.target_type !== "shipping_line" && discountData.value === "0.0") {
        this.classList.add("coupon-active-zero-coupon");
      }

      let discount = 0;
      if (discountData.allocation_method == "each") {
        let totalDiscount = 0;

        const productsAtInCart = Array.from(this.cartDrawer.querySelectorAll(".cart-items .cart-item")).filter(
          (productEl) => {
            const colectionsIds = productEl
              .getAttribute("data-colectionsids")
              .split(",")
              .filter((id) => id);
            const productid = productEl.getAttribute("data-productid");
            const variantid = productEl.getAttribute("data-variantid");

            const hasInColectionId = colectionsIds.some((item) =>
              discountData.entitled_collection_ids.includes(Number(item)),
            );
            const hasInProduct = discountData.entitled_product_ids.includes(Number(productid));
            const hasInVariant = discountData.entitled_variant_ids.includes(Number(variantid));

            return hasInColectionId || hasInProduct || hasInVariant;
          },
        );

        if (productsAtInCart.length > 0) {
          productsAtInCart.forEach((product) => {
            // const productkey = product.getAttribute("data-key");
            const productid = product.getAttribute("data-productid");
            const variantid = product.getAttribute("data-variantid");

            const priceProduct = oldCart.items.find((item) => {
              // if (item.properties._key && productkey) return item.properties._key == productkey;
              return item.product_id == productid || item.variant_id == variantid;
            });

            if (priceProduct) {
              let priceDiscount = 0;
              if (discountData.value_type == "percentage") {
                priceDiscount = (priceProduct.final_price / 100) * Math.abs(discountData.value);
              } else {
                priceDiscount = Math.abs(discountData.value) * 100;
              }

              totalDiscount += priceDiscount * priceProduct.quantity;
              const priceWrapper = product.querySelector(`.cart-item__price-wrapper`);
              if (priceWrapper) priceWrapper.querySelector(".price--end").classList.add("price-without-discount");

              this.updatePriceItemCart(priceProduct, priceDiscount, priceWrapper, this.dataPromotionCoupon.code);
            }
          });
        }
        discount = totalDiscount;
      } else {
        if (discountData.value_type == "percentage") {
          discount = (oldCart.original_total_price / 100) * Math.abs(discountData.value);
          oldCart.items.forEach((product) => {
            const discountItem = (product.final_price / 100) * Math.abs(discountData.value);
            const priceWrapper = this.cartDrawer.querySelector(
              (() => {
                // if (product.properties._key) return `[data-key="${product.properties._key}"] .cart-item__price-wrapper`;
                return `[data-variantid="${product.variant_id}"] .cart-item__price-wrapper`;
              })(),
            );

            this.updatePriceItemCart(product, discountItem, priceWrapper, this.dataPromotionCoupon.code);
          });
        } else {
          discount = Math.abs(discountData.value) * 100;
        }
      }

      this.querySelector(".text-price-with-discount").innerHTML = `- ${this.formatMoney(discount)}`;

      const priceWithDiscount = oldCart.original_total_price - discount;

      if (
        !oldCart.cart_level_discount_applications.some((discount) => discount.title === this.dataPromotionCoupon.code)
      )
        document.querySelector(".subtotal .subtotal__header--value").innerHTML = this.formatMoney(priceWithDiscount);
    } else {
      if (this.dataPromotionCoupon.discount_config) {
        this.classList.add("coupon-active-zero-coupon");
      }
    }
  }

  updatePriceItemCart(product, discountItem, element, discountCode) {
    if (!element) return;
    element.querySelector(".price--end").classList.add("price-without-discount");
    const toHide = element.querySelector(".price--end:not(.price-without-discount)");
    if (toHide) {
      toHide.style.display = "none";
      toHide.classList.add("hidded-price");
    }
    let spanDiscount = element.querySelector(".price-added-by-app");
    if (!spanDiscount) {
      spanDiscount = document.createElement("span");
      spanDiscount.classList.add("price-with-discount");
      spanDiscount.classList.add("price-added-by-app");
      element.appendChild(spanDiscount); // Adicionar o span ao elemento
    }
    const hasDiscountApplied = product.discounts.some(
      (discount) => discount.title === discountCode && !(discount.amount === 0 && discount.title.includes("ESPECIAL")),
    );

    const newPrice = (product.line_price - (hasDiscountApplied ? 0 : discountItem)) * product.quantity;
    const currentPrice = parseFloat(spanDiscount.innerHTML.replace("R$ ", "").replace(",", "."));
    if (isNaN(currentPrice) || newPrice < currentPrice * 100) {
      spanDiscount.innerHTML = `${this.formatMoney(newPrice)}`;
    }
  }

  clearDiscount() {
    this.getCart((cart) => {
      this.classList.remove("coupon-active");
      this.classList.remove("coupon-active-free-shipping");
      this.classList.remove("coupon-active-zero-coupon");
      this.querySelector(".text-promotion-code").innerHTML = "";
      this.querySelector(".text-price-with-discount").innerHTML = "";
      this.cartDrawer.querySelectorAll(".hidded-price").forEach((element) => {
        element.style.display = "block";
        element.classList.remove("hidded-price");
      });
      Array.from(this.cartDrawer.querySelectorAll(".cart-item")).map((product) => {
        const priceWrapper = product.querySelector(`.cart-item__price-wrapper`);
        const element = priceWrapper.querySelector(".price--end");
        const toRemove = product.querySelector(".price-added-by-app");
        if (toRemove) toRemove.remove();
        element.classList.remove("price-without-discount");
        element.classList.remove("price-added-by-app");
      });
      if (this.dataPromotionCoupon && this.dataPromotionCoupon.oldCart) {
        this.dataPromotionCoupon.oldCart.items.forEach((product) => {
          const priceWrapper = this.cartDrawer.querySelector(
            (() => {
              // if (product.properties._key) return `[data-key="${product.properties._key}"] .cart-item__price-wrapper`;
              return `[data-variantid="${product.variant_id}"] .cart-item__price-wrapper`;
            })(),
          );
          this.updatePriceItemCart(product, 0, priceWrapper, "");
        });
      }

      fetch(`/discount/REMOVE_DISCOUNT_CODES?v=${Date.now()}`, {}).finally(() => {
        document.querySelector(".subtotal .subtotal__header--value").innerHTML = this.formatMoney(
          cart.original_total_price,
        );
        const sessionData = JSON.parse(sessionStorage.getItem("insider:cart:promotionCoupon"));
        delete sessionData.data;
        delete sessionData.code;
        delete sessionData.oldCart;
        this.dataPromotionCoupon = sessionData;

        if (sessionData) {
          sessionStorage.setItem(
            "insider:cart:promotionCoupon",
            JSON.stringify({ ...this.dataPromotionCoupon, oldCart: cart }),
          );
        }
      });
    });
  }

  getCart = async (callback) => {
    const response = await fetch("/cart.js", { headers: { "Cache-Control": "max-age=5" } });
    const data = await response.json();
    callback(data);
  };

  addPromotionCoupon(e, forcePromotionCoupon) {
    e?.preventDefault();

    const URL_COUPON_FORCE_KEY = "insider:cart:couponFromUrl:force";
    const forcedFromJourney = sessionStorage.getItem(URL_COUPON_FORCE_KEY) === "true";

    const promotionCoupon = forcePromotionCoupon ?? this.inputPromotionCoupon.value.toUpperCase();
    if (!promotionCoupon) {
      this.inputPromotionCoupon.focus();
      return;
    }

    if (!forcePromotionCoupon) {
      window.dataLayer.push({
        event: `click_apply_coupon_in_cart`,
        timestamp: new Date().toISOString(),
        promotionCoupon: promotionCoupon,
      });
    } else {
      window.dataLayer.push({
        event: `auto_apply_coupon_in_cart`,
        timestamp: new Date().toISOString(),
        promotionCoupon: promotionCoupon,
      });
    }

    this.applyCouponBtn.classList.add("loading");

    const discountsCuponsArray = Object.keys(this.dataPromotionCoupon.discounts_cupons.discount_codes);
    const discountsCuponsArrayFiltered = discountsCuponsArray.filter((discount) =>
      promotionCoupon.includes(discount.toLocaleUpperCase()),
    );

    if (discountsCuponsArrayFiltered.length === 0) {
      this.getDataCouponDiscount(promotionCoupon);
    } else {
      const sessionData = JSON.parse(sessionStorage.getItem("insider:cart:promotionCoupon"));
      this.dataPromotionCoupon = {
        discount_config: sessionData.discounts_cupons.discount_codes[promotionCoupon],
        discounts_cupons: sessionData.discounts_cupons,
        data: {},
        code: promotionCoupon,
        force: sessionData?.force === true || forcedFromJourney,
        oldCart: null,
      };

      sessionStorage.setItem("insider:cart:promotionCoupon", JSON.stringify(this.dataPromotionCoupon));
      this.getCart(async (cart) => {
        this.insertCouponInCheckout(promotionCoupon);
        this.promotionCouponActive(cart);
      });
    }

    this.applyCouponBtn.classList.remove("loading");
  }

  insertCouponInCheckout(promotionCoupon) {
    const discountApplyUrl = `/discount/${promotionCoupon}?v=${Date.now()}`;
    fetch(discountApplyUrl, {}).then(function (response) {
      return response.text();
    });
  }

  getDataCouponDiscount(promotionCoupon) {
    this.getCart(async (cart) => {
      const URL_COUPON_FORCE_KEY = "insider:cart:couponFromUrl:force";
      const forcedFromJourney = sessionStorage.getItem(URL_COUPON_FORCE_KEY) === "true";
      const body = {
        code: promotionCoupon.trim(),
        cart: cart,
        customer: (() => {
          if (this.customerId) {
            return {
              id: this.customerId,
            };
          }
          return null;
        })(),
      };

      try {
        await fetch(
          "https://dc-validate-discount-code.insiderstoreapis.com/v1/discount-code",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
          },
        ).then((response) => {
          response.json().then((data) => {
            if (!data.valid) {
              if (data?.discount) {
                this.setMessages("Este desconto atingiu o limite de uso.");
              } else {
                this.setMessages("Insira um código de desconto ou cartão-presente válido.");
              }
            } else {
              const sessionData = JSON.parse(sessionStorage.getItem("insider:cart:promotionCoupon"));

              this.dataPromotionCoupon = {
                discounts_cupons: sessionData.discounts_cupons,
                data,
                code: promotionCoupon,
                force: sessionData?.force === true || forcedFromJourney,
                oldCart: cart,
              };
              sessionStorage.setItem(
                "insider:cart:promotionCoupon",
                JSON.stringify({ ...this.dataPromotionCoupon, oldCart: cart }),
              );

              this.promotionCouponActive(cart);
              this.insertCouponInCheckout(promotionCoupon);
            }
          });
        });
      } catch (error) {
        console.error("Error:", error);
      }
    });

    return true;
  }

  setMessages(message) {
    this.querySelector(".promotion-coupon__result-message").classList.add("active");
    this.querySelector(".promotion-coupon__message").innerHTML = message;

    setTimeout(() => {
      this.querySelector(".promotion-coupon__result-message").classList.remove("active");
    }, 10000);
  }
}

customElements.define("promotion-coupon", PromotionCoupon);

// Cart Drawer Tiered Promo messages (moved from Liquid inline script)
(function initCartProgressMessages() {
  if (window.__insider_cartProgressInit) return; // prevent double init on re-renders
  window.__insider_cartProgressInit = true;

  function parseDiscountTiers(rawTiers) {
    var list = rawTiers || [];
    if (!Array.isArray(list)) list = [list];
    return list
      .map(function (t) {
        var required = Number(t.quantityRequiredForDiscount ?? t.minimumToBuy ?? t.required);
        var rawDiscount = t.percentualDiscount ?? t.discountPercentage;
        var discount = rawDiscount != null ? Number(rawDiscount) : null;
        if (Number.isNaN(required) || required <= 0) return null;
        if (discount === null || Number.isNaN(discount) || discount <= 0) return null;
        return { required: required, discount: discount, type: "discount" };
      })
      .filter(Boolean)
      .sort(function (a, b) {
        return a.required - b.required;
      });
  }

  function parseGiftTiers(rawTiers) {
    var list = rawTiers || [];
    if (!Array.isArray(list)) list = [list];
    return list
      .map(function (t) {
        // Support multiple schemas for Buy X Get Y style promos
        var required =
          t.quantityRequiredToBeGifted ?? t.quantityRequired ?? t.buyQuantity ?? t.minimumToBuy ?? t.x ?? t.required;
        var gifted = t.quantityToBeGifted ?? t.getQuantity ?? t.y ?? t.quantityFree ?? t.gifted ?? 1;
        var reqNum = Number(required);
        var giftNum = Number(gifted);
        if (Number.isNaN(reqNum) || reqNum <= 0) return null;
        var safeGifted = !Number.isNaN(giftNum) && giftNum > 0 ? giftNum : 1;
        return { required: reqNum, gifted: safeGifted, type: "gift" };
      })
      .filter(Boolean)
      .sort(function (a, b) {
        return a.required - b.required;
      });
  }

  function normalizeTiers(rawTiers) {
    var discountTiers = parseDiscountTiers(rawTiers);
    if (discountTiers.length) return discountTiers;
    return parseGiftTiers(rawTiers);
  }

  function isDiscountMode(tiers) {
    return (
      Array.isArray(tiers) &&
      tiers.some(function (t) {
        return t.type === "discount" && (t.discount || 0) > 0;
      })
    );
  }

  function groupHasUnderlyingMarkdown(groupEl) {
    var referenceTotal = 0;
    var baseTotal = 0;
    var hasComparableData = false;

    groupEl.querySelectorAll(":scope > .cart-item").forEach(function (itemEl) {
      var referenceLine = Number(
        itemEl.getAttribute("data-reference-line-price") || 0
      );
      var baseLine = Number(
        itemEl.getAttribute("data-base-line-price") || 0
      );

      if (
        Number.isFinite(referenceLine) &&
        Number.isFinite(baseLine) &&
        referenceLine > 0 &&
        baseLine > 0
      ) {
        hasComparableData = true;
        referenceTotal += referenceLine;
        baseTotal += baseLine;
      }
    });

    return hasComparableData && referenceTotal > baseTotal;
  }

  function computeDiscountMessage(currentQuantity, tiers, shouldSayAdditional) {
    if (!tiers || !tiers.length) return "";
    var nextEligibleTier = null;
    var highestUnlockedTier = null;

    for (var i = 0; i < tiers.length; i++) {
      var tier = tiers[i];
      if (currentQuantity < tier.required && !nextEligibleTier) nextEligibleTier = tier;
      if (currentQuantity >= tier.required) highestUnlockedTier = tier;
    }

    var parts = [];

    if (highestUnlockedTier && (highestUnlockedTier.discount || 0) > 0) {
      var currPct = Math.floor(Number(highestUnlockedTier.discount || 0));
      var additionalLabel = shouldSayAdditional ? " + desconto adicional" : "";
      parts.push(`Seu kit tem <strong>${currPct}% OFF</strong>${additionalLabel}`);
    }

    if (nextEligibleTier) {
      var missing = nextEligibleTier.required - currentQuantity;
      var nextPct = Math.floor(Number(nextEligibleTier.discount || 0));
      parts.push(
        "Desbloqueie " +
          nextPct +
          "% OFF com mais " +
          missing +
          " unidade" +
          (missing !== 1 ? "s" : "") +
          " no carrinho",
      );
    }

    if (!parts.length && highestUnlockedTier && (highestUnlockedTier.discount || 0) > 0) {
      var pct2 = Math.floor(Number(highestUnlockedTier.discount || 0));
      parts.push(
        "Desconto máximo desbloqueado. Aproveite seus " +
          pct2 +
          "% OFF com mais itens tecnológicos",
      );
    }

    return parts.join(". ");
  }

  function computePartialBuyAndSaveMessage(currentQuantity, tiers, composition) {
    if (!tiers || !tiers.length) return "";
    var nextEligibleTier = null;
    var highestUnlockedTier = null;

    for (var i = 0; i < tiers.length; i++) {
      var tier = tiers[i];
      if (currentQuantity < tier.required && !nextEligibleTier) nextEligibleTier = tier;
      if (currentQuantity >= tier.required) highestUnlockedTier = tier;
    }

    var discountedWeight = composition.reduce(function (weight, part) {
      if (weight > 0) return weight;
      var candidate = Number((part && part.discountPercentageWeight) || 0);
      return candidate > 0 ? candidate : 0;
    }, 0);

    var parts = [];
    if (highestUnlockedTier && (highestUnlockedTier.discount || 0) > 0) {
      var currentPct = Math.floor(
        Number(highestUnlockedTier.discount || 0) * discountedWeight / 100,
      );
      parts.push("Promoção desbloqueada: " + currentPct + "% OFF na segunda peça");
    }

    if (nextEligibleTier) {
      var missing = nextEligibleTier.required - currentQuantity;
      var nextPct = Math.floor(
        Number(nextEligibleTier.discount || 0) * discountedWeight / 100,
      );
      parts.push(
        "Desbloqueie " +
          nextPct +
          "% OFF na segunda peça com mais " +
          missing +
          " unidade" +
          (missing !== 1 ? "s" : "") +
          " no carrinho",
      );
    }

    return parts.join(" ");
  }

  function isPartialBuyAndSave(promoType, currentQuantity, tiers, composition) {
    if (promoType !== "buy-and-save" || !Array.isArray(composition) || !composition.length) return false;

    var activeTier = highestUnlockedDiscountTier(currentQuantity, tiers);
    var activeDiscount = activeTier ? Number(activeTier.discount || 0) : 0;
    if (activeDiscount <= 0) return false;

    return composition.some(function (part) {
      var weight = Number((part && part.discountPercentageWeight) || 0);
      return weight > 0 && (weight < 100 || activeDiscount < 100);
    });
  }

  function computeGiftMessage(currentQuantity, tiers) {
    if (!tiers || !tiers.length) return "";
    var nextEligibleTier = null;
    var highestUnlockedTier = null;
    for (var i = 0; i < tiers.length; i++) {
      var tier = tiers[i];
      if (currentQuantity < tier.required && !nextEligibleTier) nextEligibleTier = tier;
      if (currentQuantity >= tier.required) highestUnlockedTier = tier;
    }
    var parts = [];
    if (highestUnlockedTier && (highestUnlockedTier.gifted || 0) > 0) {
      var giftedLabelCurr = highestUnlockedTier.gifted === 1 ? "1 peça" : String(highestUnlockedTier.gifted) + " peças";
      parts.push("Promoção desbloqueada: leve " + giftedLabelCurr + " grátis");
    }
    if (nextEligibleTier) {
      var missing = nextEligibleTier.required - currentQuantity;
      var giftedLabelNext = nextEligibleTier.gifted === 1 ? "1 peça" : String(nextEligibleTier.gifted) + " peças";
      parts.push(
        "Com mais " +
          missing +
          " peça" +
          (missing !== 1 ? "s" : "") +
          ", você desbloqueia " +
          giftedLabelNext +
          " grátis no carrinho",
      );
    }
    if (!parts.length && highestUnlockedTier && (highestUnlockedTier.gifted || 0) > 0) {
      // var giftedLabelMax = highestUnlockedTier.gifted === 1 ? '1 peça' : String(highestUnlockedTier.gifted) + ' peças';
      parts.push("Máximo de peças gratuitas selecionadas");
    }
    return parts.join(" ");
  }

  function computePromoMessage(qty, tiers, promoType, basInfo, shouldSayAdditional, composition) {
    if (promoType === "buy-and-save" || promoType === "buy-x-pay-y") {
      // A composição ponderada também pode representar desconto parcial
      // (por exemplo, uma regra de 50% com peso 100). Só tratamos a mecânica
      // como brinde quando o desconto efetivo da composição chega a 100%.
      var hasGiftComposition = basInfo && basInfo.gifted > 0;
      if (isDiscountMode(tiers) && !hasGiftComposition) {
        if (isPartialBuyAndSave(promoType, qty, tiers, composition)) {
          return computePartialBuyAndSaveMessage(qty, tiers, composition);
        }
        return computeDiscountMessage(qty, tiers, shouldSayAdditional);
      }

      var giftTiers = buildBuyAndSaveTiers(tiers, basInfo);
      if (giftTiers.length) return computeGiftMessage(qty, giftTiers);
      return "";
    }
    if (!tiers || !tiers.length) return "";
    return isDiscountMode(tiers)
      ? computeDiscountMessage(qty, tiers, shouldSayAdditional)
      : computeGiftMessage(qty, tiers);
  }

  // For free-product buy-and-save kits the promo is "buy N, get M free" —
  // the rules array doesn't carry that semantic, but the helper exposes it via
  // data-bas-required / data-bas-gifted (derived from productComposition).
  // We turn that into a single gift tier the gift-message formatter accepts.
  function buildBuyAndSaveTiers(tiers, basInfo) {
    if (basInfo && basInfo.required > 0 && basInfo.gifted > 0) {
      return [
        {
          required: basInfo.required,
          gifted: basInfo.gifted,
          type: "gift",
        },
      ];
    }
    // Fallback: legacy buy_and_save metafield already comes shaped as gift tiers.
    var giftTiers = parseGiftTiers(tiers);
    return giftTiers;
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this,
        args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function highestUnlockedDiscountTier(qty, tiers) {
    if (!tiers || !tiers.length) return null;
    var unlocked = null;
    for (var i = 0; i < tiers.length; i++) {
      var tier = tiers[i];
      if (tier.type !== "discount") continue;
      if (qty >= tier.required && (tier.discount || 0) > 0) {
        if (!unlocked || tier.discount > unlocked.discount) unlocked = tier;
      }
    }
    return unlocked;
  }

  // Marker used to identify badges injected by JS, so we never overwrite
  // a badge already rendered by Liquid from the legacy discount.title.
  var BADGE_JS_MARKER = "data-kit-badge-source";

  // Generic per-item badge synchronizer. Idempotent, scoped to the given group,
  // and respects badges rendered by Liquid (legacy path) — the marker attribute
  // distinguishes JS-managed badges from Liquid ones, so the two never fight.
  function syncItemBadges(groupEl, badgeClass, label) {
    var items = groupEl.querySelectorAll(":scope > .cart-item");
    Array.prototype.forEach.call(items, function (itemEl) {
      var list = itemEl.querySelector(".cart-item__details > ul.discounts");
      if (!list) return;

      var jsBadge = list.querySelector("." + badgeClass + "[" + BADGE_JS_MARKER + '="js"]');
      var commercialLiquidBadge = list.querySelector(
        "." + badgeClass + "[data-commercial-kit-badge]",
      );
      var legacyLiquidBadge = list.querySelector(
        "." +
          badgeClass +
          ":not([" +
          BADGE_JS_MARKER +
          "]):not([data-commercial-kit-badge])",
      );

      // The mono/multi kit commercial badge is server-rendered as a stable
      // fallback, but JS owns its live label because it recalculates the
      // perceived group discount from reference -> commercial prices.
      if (commercialLiquidBadge) {
        if (jsBadge) jsBadge.remove();

        if (!label) {
          commercialLiquidBadge.hidden = true;
          commercialLiquidBadge.textContent = "";
          return;
        }

        if (commercialLiquidBadge.textContent !== label) {
          commercialLiquidBadge.textContent = label;
        }
        commercialLiquidBadge.hidden = false;
        return;
      }

      // Legacy Liquid badges keep their original ownership.
      if (legacyLiquidBadge) {
        if (jsBadge) jsBadge.remove();
        return;
      }

      if (!label) {
        if (jsBadge) jsBadge.remove();
        return;
      }

      if (jsBadge) {
        if (jsBadge.textContent !== label) jsBadge.textContent = label;
        return;
      }

      var badge = document.createElement("li");
      badge.className = "discounts__discount " + badgeClass;
      badge.setAttribute(BADGE_JS_MARKER, "js");
      badge.textContent = label;
      list.appendChild(badge);
    });
  }

  function getGroupPerceivedDiscountPct(groupEl) {
    var referenceTotal = 0;
    var commercialTotal = 0;
    var hasPricing = false;

    Array.prototype.slice
      .call(groupEl.querySelectorAll(":scope > .cart-item"))
      .forEach(function (itemEl) {
        var referenceLine = Number(
          itemEl.getAttribute("data-reference-line-price") || 0,
        );

        var commercialLine = Number(
          itemEl.getAttribute("data-commercial-line-price") || 0,
        );

        if (
          Number.isFinite(referenceLine) &&
          Number.isFinite(commercialLine) &&
          referenceLine > 0 &&
          commercialLine >= 0
        ) {
          referenceTotal += referenceLine;
          commercialTotal += commercialLine;
          hasPricing = true;
        }
      });

    if (
      !hasPricing ||
      referenceTotal <= 0 ||
      commercialTotal >= referenceTotal
    ) {
      return 0;
    }

    // Mesma fronteira visual da PDK:
    // soma primeiro, arredonda uma vez e então calcula a comunicação.
    var referenceDisplay = Math.ceil(referenceTotal / 100);
    var commercialDisplay = Math.ceil(commercialTotal / 100);

    if (commercialDisplay >= referenceDisplay) return 0;

    var percentage =
      ((referenceDisplay - commercialDisplay) / referenceDisplay) * 100;

    return Math.floor(
      Math.round(percentage * 1000000) / 1000000,
    );
  }

  function syncKitBadges(groupEl, unlockedPct, promoType, schemaType) {
    if (promoType !== "kit") return;
    
    if (schemaType === "progressive-discount") {
      var label = unlockedPct > 0 ? `${unlockedPct}% OFF` : "";
    } else {
      var label = unlockedPct > 0 ? `KIT ${unlockedPct}% OFF` : "";
    }
    syncItemBadges(groupEl, "discounts__discount--desconto-kit", label);
  }

  function syncGiftMechanicBadges(groupEl, basInfo, currentQuantity, promoType, tiers, composition) {
    if (promoType !== "buy-and-save" && promoType !== "buy-x-pay-y") return;
    if (isPartialBuyAndSave(promoType, currentQuantity, tiers, composition)) return;

    var label = promoType === "buy-x-pay-y"
      ? "Leve e Pague"
      : "Compre e Ganhe";

    syncItemBadges(groupEl, "discounts__discount--buy-and-save", label);
  }

  function refreshMessages() {
    var groups = Array.prototype.slice.call(document.querySelectorAll(".cart-promo-group"));
    if (!groups.length) return;

    groups.forEach(function (el) {
      var tiersAttr = el.getAttribute("data-tiers");
      var promoProductList = el.getAttribute("data-promo-product-list");
      var promoType = el.getAttribute("data-promo-type") || "";
      var schemaType = el.getAttribute("data-schema-type") || "";
      var msg = [];

      if (tiersAttr) {
        var totalQty = Array.from(el.querySelectorAll(":scope > .cart-item"))
          .map(function (itemEl) {
            var lineQty = Number(itemEl.getAttribute("data-line-quantity") || 0);
            if (Number.isFinite(lineQty) && lineQty >= 0) return lineQty;

            var qtyInput = itemEl.querySelector(".quantity__input");
            return qtyInput
              ? Number(qtyInput.value || qtyInput.getAttribute("value") || 0)
              : 0;
          })
          .reduce(function (a, b) {
            return a + b;
          }, 0);

        var raw;
        try {
          raw = JSON.parse(tiersAttr);
        } catch (e) {
          raw = [];
        }
        var tiers = normalizeTiers(raw);
        var composition = [];
        var compositionAttr = el.getAttribute("data-promo-composition");
        if (compositionAttr) {
          try {
            composition = JSON.parse(compositionAttr);
          } catch (e) {
            composition = [];
          }
        }
        var basInfo = {
          required: Number(el.getAttribute("data-bas-required") || 0),
          gifted: Number(el.getAttribute("data-bas-gifted") || 0),
        };

        var progressQty = totalQty;
        if (schemaType === "multi-product-kit") {
          var resolvedProgressQty = Number(el.getAttribute("data-progress-quantity") || 0);
          if (Number.isFinite(resolvedProgressQty) && resolvedProgressQty > 0) {
            progressQty = resolvedProgressQty;
          }
        }

        var shouldSayAdditional = groupHasUnderlyingMarkdown(el);
        msg.push(
          computePromoMessage(
            progressQty,
            tiers,
            promoType,
            basInfo,
            shouldSayAdditional,
            composition,
          ),
        );

        var unlocked = highestUnlockedDiscountTier(progressQty, tiers);
        var unlockedPct = unlocked ? Math.floor(Number(unlocked.discount || 0)) : 0;

        var badgePct = unlockedPct;
        if (schemaType === "mono-product-kit" || schemaType === "multi-product-kit") {
          var perceivedPct = getGroupPerceivedDiscountPct(el);
          if (perceivedPct > 0) badgePct = perceivedPct;
        }

        syncKitBadges(el, badgePct, promoType, schemaType);
        syncGiftMechanicBadges(el, basInfo, progressQty, promoType, tiers, composition);
      }

      if (promoProductList) {
        var itemsLinkLabel =
          schemaType === "progressive-discount"
            ? "Ver itens disponíveis"
            : "Ver itens";

        msg.push(
          `<a href="/${promoProductList}">${itemsLinkLabel}</a>`,
        );
      }
      msg = msg.filter(Boolean).join(". ");

      var target = el.querySelector(".promo-group__progress-message");
      if (target && target.innerHTML !== msg) {
        target.innerHTML = msg || "";
      }
    });
  }

  window.refreshCupinnCartPromoMessages = refreshMessages;

  // initial + event wiring
  document.addEventListener("DOMContentLoaded", refreshMessages, { once: true });
  refreshMessages();
  ["cart:updated", "cart:change", "cart:refresh", "cart:requestComplete", "quantity:change", "cart:combine-organized"].forEach(function (evt) {
    document.addEventListener(evt, refreshMessages);
  });

  var debouncedRefresh = debounce(refreshMessages, 120);

  // Delegate clicks on plus/minus buttons
  document.addEventListener("click", function (e) {
    var btn = e.target.closest(".quantity__button");
    if (btn) {
      setTimeout(debouncedRefresh, 120);
    }
  });

  // Listen to direct edits on number inputs (if enabled)
  document.addEventListener("input", function (e) {
    if (e.target && e.target.classList && e.target.classList.contains("quantity__input")) {
      debouncedRefresh();
    }
  });

  // Observe quantity value attribute/text changes within the cart drawer
  var cartRoot = document.querySelector("#CartDrawer");
  if (cartRoot && "MutationObserver" in window) {
    var mo = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var m = muts[i];
        if (
          m.type === "attributes" &&
          m.target.classList &&
          m.target.classList.contains("quantity__input") &&
          m.attributeName === "value"
        ) {
          debouncedRefresh();
          break;
        }
        if (m.type === "childList") {
          debouncedRefresh();
          break;
        }
      }
    });
    mo.observe(cartRoot, { subtree: true, childList: true, attributes: true, attributeFilter: ["value"] });
  }
})();


(function initCupinnQuantityChangeGuard() {
  if (window.__insider_cupinnQuantityChangeGuardInit) return;
  window.__insider_cupinnQuantityChangeGuardInit = true;

  document.addEventListener(
    "change",
    function (event) {
      var input = event.target.closest("#CartDrawer .quantity__input");
      if (!input) return;

      var itemEl = input.closest(".cart-item");
      if (!itemEl) return;

      var mode = itemEl.getAttribute("data-quantity-mode") || "native";
      if (mode === "native") return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    },
    true
  );
})();

(function initCupinnGroupRemoveController() {
  if (window.__insider_cupinnGroupRemoveInit) return;
  window.__insider_cupinnGroupRemoveInit = true;

  function renderCartSections(state) {
    var cartDrawer = document.querySelector("cart-drawer");

    if (
      !cartDrawer ||
      typeof cartDrawer.renderContents !== "function"
    ) {
      return Promise.reject(
        new Error(
          "CartDrawer.renderContents indisponivel durante cupinn-group-remove.",
        ),
      );
    }

    if (!state || !state.sections) {
      return Promise.reject(
        new Error(
          "Sections do carrinho indisponiveis durante cupinn-group-remove.",
        ),
      );
    }

    // A resposta do /cart/update.js já contém as sections atualizadas.
    // Renderiza uma única vez, sem fazer outro GET.
    cartDrawer.renderContents(state, false);

    document.dispatchEvent(
      new CustomEvent("cart:updated", {
        detail: { cart: state },
      }),
    );

    if (
      typeof publish === "function" &&
      window.PUB_SUB_EVENTS &&
      PUB_SUB_EVENTS.cartUpdate
    ) {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: "cart-items",
        cartData: state,
      });
    }

    return Promise.resolve(state);
  }

  function postGroupRemoval(groupEl, itemEl) {
    var updates = {};
    var sourceGroupId = itemEl && itemEl.getAttribute("data-source-promo-group-id");
    var itemElements = sourceGroupId
      ? document.querySelectorAll("#CartDrawer .cart-item[data-source-promo-group-id]")
      : groupEl.querySelectorAll(".cart-item[data-cart-key]");

    itemElements.forEach(function (candidateEl) {
      if (
        sourceGroupId &&
        candidateEl.getAttribute("data-source-promo-group-id") !== sourceGroupId
      ) return;

      var key = candidateEl.getAttribute("data-cart-key");
      if (key) updates[key] = 0;
    });

    if (!Object.keys(updates).length) return Promise.resolve();

    return fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        updates: updates,
        sections: ["cart-drawer", "cart-icon-bubble"],
        sections_url: "/",
      }),
    })
      .then(function (response) {
        return response.text().then(function (raw) {
          var state = null;

          try {
            state = raw ? JSON.parse(raw) : null;
          } catch (error) {
            throw new Error(
              "Resposta invalida do carrinho (" +
                response.status +
                "): " +
                raw,
            );
          }

          if (!response.ok || (state && state.errors)) {
            var message =
              (state &&
                (state.description ||
                  state.message ||
                  state.errors)) ||
              ("HTTP " + response.status);

            throw new Error(String(message));
          }

          return state;
        });
      })
      .then(renderCartSections);
  }

  document.addEventListener(
    "click",
    function (event) {
      var removeButton = event.target.closest(
        "#CartDrawer cart-remove-button button, #CartDrawer cart-remove-button",
      );
      if (!removeButton) return;

      var itemEl = removeButton.closest(".cart-item");
      var groupEl = removeButton.closest(".cart-promo-group, .cart-combine-group");

      if (!itemEl || !groupEl) return;

      var schemaType =
        itemEl.getAttribute("data-source-schema-type") ||
        groupEl.getAttribute("data-schema-type") ||
        "";

      var isGroupedMechanic =
        schemaType === "mono-product-kit" ||
        schemaType === "multi-product-kit" ||
        schemaType === "buy-x-pay-y" ||
        schemaType === "buy-and-save";

      if (!isGroupedMechanic) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      groupEl.setAttribute("data-updating", "true");

      groupEl.querySelectorAll("button").forEach(function (button) {
        button.disabled = true;
      });

      postGroupRemoval(groupEl, itemEl).catch(function (error) {
        console.error("Cupinn group removal failed:", error);

        groupEl.removeAttribute("data-updating");

        groupEl.querySelectorAll("button").forEach(function (button) {
          button.disabled = false;
        });
      });
    },
    true,
  );
})();

(function initCupinnCartQuantityControllers() {
  if (window.__insider_cupinnQuantityControllersInit) return;
  window.__insider_cupinnQuantityControllersInit = true;

  function sectionIds() {
    return ["cart-drawer", "cart-icon-bubble"];
  }

  function requestConfig() {
    return {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };
  }

  function replaceDrawerSections(state) {
    var cartDrawer = document.querySelector("cart-drawer");

    if (!cartDrawer) {
      return Promise.reject(
        new Error(
          "CartDrawer indisponivel durante cupinn-cart-controller.",
        ),
      );
    }

    function publishCartState() {
      document.dispatchEvent(
        new CustomEvent("cart:updated", { detail: { cart: state } }),
      );

      if (
        typeof publish === "function" &&
        window.PUB_SUB_EVENTS &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cupinn-cart-controller",
          cartData: state,
        });
      }

      return state;
    }

    if (
      state &&
      state.sections &&
      typeof cartDrawer.renderContents === "function"
    ) {
      cartDrawer.renderContents(state, false);
      return Promise.resolve(publishCartState());
    }

    // Defensive fallback for responses without bundled sections.
    if (typeof cartDrawer.updateCart !== "function") {
      return Promise.reject(
        new Error(
          "CartDrawer.updateCart indisponivel durante cupinn-cart-controller.",
        ),
      );
    }

    return cartDrawer.updateCart(false).then(function () {
      return publishCartState();
    });
  }

  function postCart(url, payload) {
    payload.sections = sectionIds();
    payload.sections_url = "/";

    return fetch(url, {
      ...requestConfig(),
      body: JSON.stringify(payload),
    })
      .then(function (response) {
        return response.text().then(function (raw) {
          var data = null;
          try {
            data = raw ? JSON.parse(raw) : null;
          } catch (parseError) {
            throw new Error("Resposta invalida do carrinho (" + response.status + "): " + raw);
          }

          if (!response.ok || (data && data.errors)) {
            var message =
              (data && (data.description || data.message || data.errors)) ||
              ("HTTP " + response.status);
            throw new Error(String(message));
          }

          return data;
        });
      })
      .then(function (state) {
        return replaceDrawerSections(state).then(function () {
          return state;
        });
      });
  }

  function getFreshCart() {
    return fetch("/cart.js", {
      credentials: "same-origin",
      headers: { "Cache-Control": "no-cache" },
    }).then(function (response) {
      return response.json();
    });
  }

  function setGroupBusy(groupEl, busy) {
    if (!groupEl) return;

    groupEl.toggleAttribute("data-updating", Boolean(busy));

    if (busy) {
      groupEl.querySelectorAll(".quantity__button").forEach(function (button) {
        button.disabled = true;
      });
      return;
    }

    // Nunca reabilita botoes cegamente depois do update.
    // O estado habilitado/disabled depende da mecanica:
    // - linha gratis de buy-x-pay-y: sempre disabled
    // - extremos da navegacao buy-x-pay-y: disabled conforme prev/next
    // - buy-and-save: todos os controles de quantidade ficam disabled
    //
    // Como o drawer pode ter sido re-renderizado, recalcula no DOM atual.
    requestAnimationFrame(function () {
      syncControllerVisibility(document);
    });
  }

  function removeWholeKit(groupEl, itemEl) {
    var updates = {};
    var sourceGroupId = itemEl && itemEl.getAttribute("data-source-promo-group-id");
    var itemElements = sourceGroupId
      ? document.querySelectorAll("#CartDrawer .cart-item[data-source-promo-group-id]")
      : groupEl.querySelectorAll(".cart-item[data-cart-key]");

    itemElements.forEach(function (candidateEl) {
      if (
        sourceGroupId &&
        candidateEl.getAttribute("data-source-promo-group-id") !== sourceGroupId
      ) return;

      var key = candidateEl.getAttribute("data-cart-key");
      if (key) updates[key] = 0;
    });
    if (!Object.keys(updates).length) return Promise.resolve();
    return postCart("/cart/update.js", { updates: updates });
  }

  function addStandaloneVariant(itemEl) {
    var variantId = Number(itemEl.getAttribute("data-variantid") || 0);
    if (!variantId) return Promise.resolve();

    return postCart("/cart/add.js", {
      items: [{ id: variantId, quantity: 1 }],
    });
  }

  function logicalProductItems(groupEl, itemEl) {
    var productId = itemEl.getAttribute("data-productid");
    return Array.prototype.slice.call(
      groupEl.querySelectorAll('.cart-item[data-productid="' + productId + '"]')
    );
  }

  function parseBuyXPayYOptions(groupEl) {
    var raw = groupEl.getAttribute("data-buy-x-pay-y-options");
    if (!raw) return [];

    try {
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];

      return parsed
        .filter(function (option) {
          return (
            option &&
            option.id != null &&
            Array.isArray(option.composition) &&
            option.composition.length > 0
          );
        })
        .map(function (option) {
          var totalQuantity = option.composition.reduce(function (sum, part) {
            var qty = Number((part && part.quantity) || 0);
            return sum + (Number.isFinite(qty) && qty > 0 ? qty : 0);
          }, 0);

          return {
            id: String(option.id),
            composition: option.composition,
            totalQuantity: totalQuantity,
          };
        })
        .filter(function (option) {
          return option.totalQuantity > 0;
        })
        .sort(function (a, b) {
          if (a.totalQuantity !== b.totalQuantity) {
            return a.totalQuantity - b.totalQuantity;
          }
          return a.id.localeCompare(b.id);
        });
    } catch (error) {
      console.error("Cupinn buy-x-pay-y options parse failed:", error);
      return [];
    }
  }

  function buyXPayYNumericProductId(value) {
    if (value == null) return "";
    var parts = String(value).split("/");
    return parts[parts.length - 1];
  }

  function splitBuyXPayYComposition(composition) {
    var targets = {};

    composition.forEach(function (part) {
      var productId = buyXPayYNumericProductId(part && part.productId);
      var quantity = Number((part && part.quantity) || 0);
      var weight = Number((part && part.discountPercentageWeight) || 0);

      if (!productId || !Number.isFinite(quantity) || quantity <= 0) return;

      if (!targets[productId]) {
        targets[productId] = { paid: 0, gift: 0 };
      }

      if (weight >= 100) {
        targets[productId].gift += quantity;
      } else {
        targets[productId].paid += quantity;
      }
    });

    return targets;
  }

  function currentBuyXPayYOptionIndex(groupEl, options) {
    var currentId = String(
      groupEl.getAttribute("data-promo-config-id") ||
      groupEl.getAttribute("data-promo-group-id") ||
      "",
    );

    return options.findIndex(function (option) {
      return option.id === currentId;
    });
  }

  function buildBuyXPayYMechanicUpdates(groupEl, cart, targetOption) {
    var targetByProduct = splitBuyXPayYComposition(
      targetOption.composition || [],
    );
    var targetProductIds = Object.keys(targetByProduct);

    if (!targetProductIds.length) return null;

    var domByKey = {};

    groupEl
      .querySelectorAll(":scope > .cart-item[data-cart-key]")
      .forEach(function (itemEl) {
        var key = itemEl.getAttribute("data-cart-key");
        if (!key) return;

        domByKey[key] = {
          productId: String(itemEl.getAttribute("data-productid") || ""),
          free:
            Number(itemEl.getAttribute("data-final-line-price") || 0) === 0,
        };
      });

    var groupCartItems = (cart.items || []).filter(function (cartItem) {
      return Boolean(domByKey[cartItem.key]);
    });

    if (!groupCartItems.length) return null;

    var byProduct = {};

    groupCartItems.forEach(function (cartItem) {
      var domInfo = domByKey[cartItem.key];
      if (!domInfo || !domInfo.productId) return;

      if (!byProduct[domInfo.productId]) {
        byProduct[domInfo.productId] = {
          paid: [],
          gift: [],
        };
      }

      byProduct[domInfo.productId][domInfo.free ? "gift" : "paid"].push(
        cartItem,
      );
    });

    for (var i = 0; i < targetProductIds.length; i++) {
      var productId = targetProductIds[i];
      var target = targetByProduct[productId];
      var current = byProduct[productId];

      if (!current) return null;
      if (target.paid > 0 && !current.paid.length) return null;
      if (target.gift > 0 && !current.gift.length) return null;
    }

    var updates = {};

    Object.keys(byProduct).forEach(function (productId) {
      var current = byProduct[productId];
      var target = targetByProduct[productId] || { paid: 0, gift: 0 };

      current.paid.forEach(function (cartItem, index) {
        updates[cartItem.key] = index === 0 ? target.paid : 0;
      });

      current.gift.forEach(function (cartItem, index) {
        updates[cartItem.key] = index === 0 ? target.gift : 0;
      });
    });

    return updates;
  }

  function changeBuyXPayY(groupEl, itemEl, delta) {
    var options = parseBuyXPayYOptions(groupEl);
    if (options.length <= 1) return Promise.resolve();

    var currentIndex = currentBuyXPayYOptionIndex(groupEl, options);
    if (currentIndex < 0) return Promise.resolve();

    var targetIndex = currentIndex + (delta > 0 ? 1 : -1);
    if (targetIndex < 0 || targetIndex >= options.length) {
      return Promise.resolve();
    }

    var targetOption = options[targetIndex];

    return getFreshCart().then(function (cart) {
      var updates = buildBuyXPayYMechanicUpdates(
        groupEl,
        cart,
        targetOption,
      );

      if (!updates || !Object.keys(updates).length) {
        console.warn(
          "Cupinn buy-x-pay-y navigation skipped: target mechanic cannot be represented by current cart lines.",
          {
            currentId:
              groupEl.getAttribute("data-promo-config-id") ||
              groupEl.getAttribute("data-promo-group-id"),
            targetId: targetOption.id,
          },
        );
        return null;
      }

      return postCart("/cart/update.js", {
        updates: updates,
      });
    });
  }

  function syncControllerVisibility(root) {
    (root || document).querySelectorAll(".cart-promo-group").forEach(function (groupEl) {
      var schemaType = groupEl.getAttribute("data-schema-type") || "";

      groupEl.querySelectorAll(".cart-item").forEach(function (itemEl) {
        var wrapper = itemEl.querySelector(".cart-item__quantity-wrapper");
        if (!wrapper) return;

        if (schemaType === "buy-and-save") {
          var buttons = itemEl.querySelectorAll(".quantity__button");
          var input = itemEl.querySelector(".quantity__input");

          wrapper.hidden = false;
          wrapper.style.removeProperty("display");

          buttons.forEach(function (button) {
            button.disabled = true;
          });

          if (input) input.disabled = true;
          return;
        }

        if (schemaType === "buy-x-pay-y") {
          var buttons = itemEl.querySelectorAll(".quantity__button");
          var input = itemEl.querySelector(".quantity__input");

          wrapper.hidden = false;
          wrapper.style.removeProperty("display");

          buttons.forEach(function (button) {
            button.disabled = true;
          });

          if (input) input.disabled = true;
          return;
        }

        if (Number(itemEl.getAttribute("data-final-line-price") || 0) === 0) {
          wrapper.hidden = true;
          return;
        }

        wrapper.hidden = false;
      });
    });
  }

  document.addEventListener(
    "click",
    function (event) {
      var button = event.target.closest("#CartDrawer .quantity__button");
      if (!button) return;

      var itemEl = button.closest(".cart-item");
      var groupEl = button.closest(".cart-promo-group, .cart-combine-group");
      if (!itemEl || !groupEl) return;

      var mode = itemEl.getAttribute("data-quantity-mode") || "native";
      if (mode === "native") return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (mode === "locked") return;

      var isPlus = button.name === "plus";
      var operation = null;

      if (mode === "kit") {
        operation = isPlus ? addStandaloneVariant(itemEl) : removeWholeKit(groupEl, itemEl);
      } else if (mode === "buy-x-pay-y") {
        operation = changeBuyXPayY(groupEl, itemEl, isPlus ? 1 : -1);
      }

      if (!operation || typeof operation.then !== "function") return;

      setGroupBusy(groupEl, true);
      operation
        .catch(function (error) {
          console.error("Cupinn cart quantity update failed:", error);
        })
        .finally(function () {
          setGroupBusy(groupEl, false);
        });
    },
    true
  );

  document.addEventListener("DOMContentLoaded", function () {
    syncControllerVisibility(document);
  });

  document.addEventListener("cart:updated", function () {
    requestAnimationFrame(function () {
      syncControllerVisibility(document);
    });
  });

  syncControllerVisibility(document);
})();

(function initCupinnGiftQuantitySync() {
  if (window.__insider_cupinnGiftQuantitySyncInit) return;
  window.__insider_cupinnGiftQuantitySyncInit = true;

  var syncing = false;
  var scheduled = null;

  function parseComposition(groupEl) {
    var raw = groupEl.getAttribute("data-promo-composition");
    if (!raw) return [];

    try {
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error("Cupinn gift composition parse failed:", error);
      return [];
    }
  }

  function numericProductId(value) {
    if (value == null) return "";
    var stringValue = String(value);
    var parts = stringValue.split("/");
    return parts[parts.length - 1];
  }

  function buildBaseComposition(composition) {
    var paidByProduct = {};
    var giftByProduct = {};

    composition.forEach(function (entry) {
      var productId = numericProductId(entry.productId);
      var quantity = Number(entry.quantity || 0);
      var weight = Number(entry.discountPercentageWeight || 0);

      if (!productId || !Number.isFinite(quantity) || quantity <= 0) return;

      var target = weight >= 100 ? giftByProduct : paidByProduct;
      target[productId] = Number(target[productId] || 0) + quantity;
    });

    return {
      paidByProduct: paidByProduct,
      giftByProduct: giftByProduct,
    };
  }

  function getGroupState(groupEl) {
    var paidByProduct = {};
    var giftLinesByProduct = {};

    groupEl.querySelectorAll(":scope > .cart-item").forEach(function (itemEl) {
      var productId = String(itemEl.getAttribute("data-productid") || "");
      var quantity = Number(itemEl.getAttribute("data-line-quantity") || 0);
      var finalLinePrice = Number(itemEl.getAttribute("data-final-line-price") || 0);
      var key = itemEl.getAttribute("data-cart-key") || "";

      if (!productId || !Number.isFinite(quantity) || quantity < 0) return;

      if (finalLinePrice === 0) {
        if (!giftLinesByProduct[productId]) giftLinesByProduct[productId] = [];
        giftLinesByProduct[productId].push({
          key: key,
          quantity: quantity,
        });
        return;
      }

      paidByProduct[productId] = Number(paidByProduct[productId] || 0) + quantity;
    });

    return {
      paidByProduct: paidByProduct,
      giftLinesByProduct: giftLinesByProduct,
    };
  }

  function getRepeatCount(basePaidByProduct, currentPaidByProduct) {
    var productIds = Object.keys(basePaidByProduct);
    if (!productIds.length) return 0;

    var repeats = null;

    productIds.forEach(function (productId) {
      var baseQty = Number(basePaidByProduct[productId] || 0);
      var currentQty = Number(currentPaidByProduct[productId] || 0);

      if (baseQty <= 0) return;

      var productRepeats = Math.floor(currentQty / baseQty);
      repeats = repeats === null
        ? productRepeats
        : Math.min(repeats, productRepeats);
    });

    return Math.max(0, Number(repeats || 0));
  }

  function buildGiftUpdates(groupEl) {
    var schemaType = groupEl.getAttribute("data-schema-type") || "";
    if (schemaType !== "buy-and-save" && schemaType !== "buy-x-pay-y") {
      return {};
    }

    var composition = parseComposition(groupEl);
    if (!composition.length) return {};

    var base = buildBaseComposition(composition);
    var giftProductIds = Object.keys(base.giftByProduct);
    if (!giftProductIds.length) return {};

    var current = getGroupState(groupEl);
    var repeats = getRepeatCount(base.paidByProduct, current.paidByProduct);
    if (repeats <= 0) return {};

    var updates = {};

    giftProductIds.forEach(function (productId) {
      var targetGiftQty = Number(base.giftByProduct[productId] || 0) * repeats;
      var giftLines = current.giftLinesByProduct[productId] || [];

      if (!giftLines.length) return;

      var currentGiftQty = giftLines.reduce(function (sum, line) {
        return sum + Number(line.quantity || 0);
      }, 0);

      if (currentGiftQty === targetGiftQty) return;

      giftLines.forEach(function (line, index) {
        if (!line.key) return;
        updates[line.key] = index === 0 ? targetGiftQty : 0;
      });
    });

    return updates;
  }

  function mergeUpdates(target, source) {
    Object.keys(source).forEach(function (key) {
      target[key] = source[key];
    });
  }

  function replaceSections(state) {
    var cartDrawer = document.querySelector("cart-drawer");

    if (!cartDrawer || typeof cartDrawer.updateCart !== "function") {
      return Promise.reject(
        new Error(
          "CartDrawer.updateCart indisponivel durante cupinn-gift-quantity-sync.",
        ),
      );
    }

    return cartDrawer.updateCart(false).then(function () {
      document.dispatchEvent(
        new CustomEvent("cart:updated", { detail: { cart: state } }),
      );

      if (
        typeof publish === "function" &&
        window.PUB_SUB_EVENTS &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cupinn-gift-quantity-sync",
          cartData: state,
        });
      }
    });
  }

  function syncGiftQuantities() {
    if (syncing) return;

    var updates = {};

    document
      .querySelectorAll("#CartDrawer .cart-promo-group")
      .forEach(function (groupEl) {
        mergeUpdates(updates, buildGiftUpdates(groupEl));
      });

    if (!Object.keys(updates).length) return;

    syncing = true;

    fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        updates: updates,
        sections: ["cart-drawer", "cart-icon-bubble"],
        sections_url: "/",
      }),
    })
      .then(function (response) {
        return response.text().then(function (raw) {
          var state = null;

          try {
            state = raw ? JSON.parse(raw) : null;
          } catch (error) {
            throw new Error(
              "Resposta invalida do carrinho (" + response.status + "): " + raw
            );
          }

          if (!response.ok || (state && state.errors)) {
            var message =
              (state && (state.description || state.message || state.errors)) ||
              ("HTTP " + response.status);
            throw new Error(String(message));
          }

          return state;
        });
      })
      .then(function (state) {
        return replaceSections(state);
      })
      .catch(function (error) {
        console.error("Cupinn gift quantity sync failed:", error);
      })
      .finally(function () {
        syncing = false;
      });
  }

  function scheduleSync() {
    if (scheduled) clearTimeout(scheduled);
    scheduled = setTimeout(function () {
      scheduled = null;
      syncGiftQuantities();
    }, 80);
  }

  document.addEventListener("DOMContentLoaded", scheduleSync, { once: true });
  document.addEventListener("cart:updated", scheduleSync);

  if (typeof subscribe === "function" && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleSync);
  }

  scheduleSync();
})();

(function initOrderLevelCouponVisualizer() {
  if (window.__insider_orderCouponInit) return;
  window.__insider_orderCouponInit = true;

  function formatMoney(cents) {
    var value = (cents / 100).toFixed(2).replace(".", ",");
    return "R$ " + value.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  }

  function getCart() {
    return fetch("/cart.js", { credentials: "same-origin", headers: { "Cache-Control": "max-age=5" } })
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        localStorage.setItem("insider:cart:freshCart", JSON.stringify(data));
        return data;
      })
      .catch(function () {
        return null;
      });
  }

  function resetLineItemPrices() {
    var priceEls = document.querySelectorAll(".cart-items .cart-item__price-wrapper .price--end");
    Array.prototype.forEach.call(priceEls, function (priceEl) {
      var original = priceEl.getAttribute("data-order-coupon-original-price");
      if (original) {
        priceEl.innerHTML = original;
      }
      priceEl.classList.remove("price-without-discount", "order-coupon-price-added");
      var wrapper = priceEl.parentElement;
      if (wrapper) {
        var extra = wrapper.querySelector(".order-coupon-price");
        if (extra) extra.remove();
      }
    });
  }

  function clearFixedDiscountRow() {
    // Cancela qualquer cadeia de retry pendente antes de remover/inserir novamente
    if (applyFixedCouponRow && applyFixedCouponRow.__retryTimeoutId) {
      clearTimeout(applyFixedCouponRow.__retryTimeoutId);
      applyFixedCouponRow.__retryTimeoutId = null;
    }

    var row = document.getElementById("order-level-fixed-discount-row");
    if (row && row.parentNode) {
      row.parentNode.removeChild(row);
    }
  }

  function findCartItem(cart, cartItemEl, usedIndexes) {
    if (!cart || !cart.items) return null;
    var productId = cartItemEl.getAttribute("data-productid");
    var variantId = cartItemEl.getAttribute("data-variantid");
    var key = cartItemEl.getAttribute("data-key");

    // Quando temos mecânicas de compre e ganhe, podemos ter múltiplos line items
    // com o mesmo product_id/variant_id. Para não sempre pegar o primeiro,
    // percorremos todos os itens que "batem" e escolhemos o primeiro ainda não usado.
    var candidates = [];
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      var match = false;

      if (key && item.properties && item.properties._key && String(item.properties._key) === String(key)) {
        match = true;
      } else if (variantId && item.variant_id && String(item.variant_id) === String(variantId)) {
        match = true;
      } else if (productId && item.product_id && String(item.product_id) === String(productId)) {
        match = true;
      }

      if (match) {
        candidates.push({ index: i, item: item });
      }
    }

    if (!candidates.length) return null;

    if (usedIndexes) {
      for (var j = 0; j < candidates.length; j++) {
        var c = candidates[j];
        if (!usedIndexes[c.index]) {
          usedIndexes[c.index] = true;
          return c.item;
        }
      }
      // Se todos os candidates já foram usados, cai no primeiro por segurança.
      usedIndexes[candidates[0].index] = true;
      return candidates[0].item;
    }

    // Comportamento "antigo" quando não usamos controle de índice
    return candidates[0].item;
  }

  function applyPercentageCoupon(cart, app) {
    if (!cart || !app) return;

    var rawValue = Number(app.value || app.percentage || 0);
    if (isNaN(rawValue) || !rawValue) return;

    var pct = Math.abs(rawValue);
    if (pct <= 0) return;

    var cartItemEls = document.querySelectorAll(".cart-items .cart-item");
    if (!cartItemEls.length) return;

    var usedCartItemIndexes = {};
    Array.prototype.forEach.call(cartItemEls, function (cartItemEl) {
      var cartItem = findCartItem(cart, cartItemEl, usedCartItemIndexes);
      if (!cartItem) return;

      var baseLine = Number(cartItem.final_line_price || cartItem.line_price || 0);
      if (!baseLine) return;

      var discountedLine = Math.round((baseLine * (100 - pct)) / 100);

      var priceWrapper = cartItemEl.querySelector(".cart-item__price-wrapper");
      if (!priceWrapper) return;

      var priceEnd = priceWrapper.querySelector(".price--end");
      if (!priceEnd) return;

      var hasOldPrice = !!priceWrapper.querySelector(".cart-item__old-price");

      if (!priceEnd.getAttribute("data-order-coupon-original-price")) {
        priceEnd.setAttribute("data-order-coupon-original-price", priceEnd.innerHTML);
      }

      // Se já existe um old price, não criamos um segundo preço com desconto.
      // Apenas sobrescrevemos o valor exibido no priceEnd com o valor já com cupom de pedido.
      if (hasOldPrice) {
        priceEnd.innerHTML = formatMoney(discountedLine);
        return;
      }

      // Caso não tenha old price, mantemos o comportamento anterior:
      // mostramos o preço original "riscado" e adicionamos um span com o preço com desconto.
      priceEnd.classList.add("price-without-discount", "order-coupon-price-added");

      var discountSpan = priceWrapper.querySelector(".order-coupon-price");
      if (!discountSpan) {
        discountSpan = document.createElement("span");
        discountSpan.className = "price-with-discount order-coupon-price";
        priceWrapper.appendChild(discountSpan);
      }

      discountSpan.innerHTML = formatMoney(discountedLine);
    });
  }

  function applyFixedCouponRow(cart, app, isRetry) {
    clearFixedDiscountRow();
    if (!cart || !app) return;
    if (app.value_type !== "fixed_amount") return;

    // Apenas quando é uma NOVA invocação (não um retry interno) cancelamos a cadeia anterior
    // e resetamos o contador. Isso permite distinguir "retry" vs "nova aplicação".
    if (!isRetry) {
      if (applyFixedCouponRow.__retryTimeoutId) {
        clearTimeout(applyFixedCouponRow.__retryTimeoutId);
        applyFixedCouponRow.__retryTimeoutId = null;
      }
      applyFixedCouponRow.__retryCount = 0;
    }

    var subtotalEl = document.querySelector(".subtotal");

    if (!subtotalEl || !subtotalEl.parentNode) {
      applyFixedCouponRow.__retryCount = (applyFixedCouponRow.__retryCount || 0) + 1;

      if (applyFixedCouponRow.__retryCount <= 20) {
        applyFixedCouponRow.__retryTimeoutId = setTimeout(function () {
          applyFixedCouponRow.__retryTimeoutId = null;
          applyFixedCouponRow(cart, app, true);
        }, 50);
      } else {
        // Evita log spam: avisa apenas uma vez por sessão quando o subtotal não aparece
        if (!applyFixedCouponRow.__warnedMissingSubtotal) {
          applyFixedCouponRow.__warnedMissingSubtotal = true;
          console.warn(
            "[insider-order-coupon] Subtotal (.subtotal) não encontrado após retries; linha de desconto fixa não foi inserida.",
            { retryCount: applyFixedCouponRow.__retryCount },
          );
        }
      }

      return;
    }

    applyFixedCouponRow.__retryCount = 0;
    applyFixedCouponRow.__retryTimeoutId = null;

    var discountCents = 0;

    if (typeof app.total_allocated_amount === "number") {
      discountCents = Math.round(Math.abs(app.total_allocated_amount));
    } else if (typeof app.total_allocated_amount === "string") {
      var num = parseFloat(app.total_allocated_amount.replace(",", "."));
      if (!isNaN(num)) {
        discountCents = Math.round(Math.abs(num) * 100);
      }
    }

    if (!discountCents && typeof cart.total_discount === "number") {
      discountCents = Math.round(Math.abs(cart.total_discount));
    }

    if (!discountCents) return;

    var row = document.createElement("div");
    row.id = "order-level-fixed-discount-row";
    row.className = "subtotal__row subtotal__row--order-discount";
    row.style.display = "flex";
    row.style.justifyContent = "space-between";
    row.style.alignItems = "center";
    row.style.padding = "0 16px";
    row.style.fontSize = "12px";
    row.innerHTML =
      '<span class="subtotal__title">Desconto do pedido</span>' +
      '<span class="subtotal__header--value subtotal__header--value--discount" style="font-size: 12px; font-weight: 600;">- ' +
      formatMoney(discountCents) +
      "</span>";

    subtotalEl.parentNode.insertBefore(row, subtotalEl);
  }

  function applyOrderCoupons(cart) {
    resetLineItemPrices();
    clearFixedDiscountRow();

    if (
      !cart ||
      !Array.isArray(cart.cart_level_discount_applications) ||
      !cart.cart_level_discount_applications.length
    ) {
      return;
    }

    var percentageApp = cart.cart_level_discount_applications.find(function (app) {
      return app.value_type === "percentage";
    });

    var fixedApp = cart.cart_level_discount_applications.find(function (app) {
      return app.value_type === "fixed_amount";
    });

    if (percentageApp) {
      applyPercentageCoupon(cart, percentageApp);
    }

    if (fixedApp) {
      applyFixedCouponRow(cart, fixedApp);
    }
  }

  function handleCartUpdate(evt) {
    var cart = (evt && (evt.cart || (evt.detail && evt.detail.cart))) || null;

    if (cart) {
      applyOrderCoupons(cart);
      return;
    }

    getCart().then(function (freshCart) {
      applyOrderCoupons(freshCart);
    });
  }

  // Garante que, após a hidratação/renderização do CartDrawer via Sections,
  // o cupom de pedido seja reaplicado aos line items.
  if (typeof CartDrawer === "function" && !CartDrawer.prototype.__orderCouponPatched) {
    CartDrawer.prototype.__orderCouponPatched = true;
    var __originalRenderContents = CartDrawer.prototype.renderContents;
    CartDrawer.prototype.renderContents = function (parsedState, shouldOpen) {
      // chama o comportamento original de renderização
      __originalRenderContents.call(this, parsedState, shouldOpen);
      // depois que o HTML do carrinho foi reidratado/renderizado,
      // buscamos o cart e reaplicamos o cupom de pedido nos line items.
      getCart().then(function (cart) {
        applyOrderCoupons(cart);
      });
    };
  }

  if (typeof subscribe === "function" && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, handleCartUpdate);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      getCart().then(function (cart) {
        applyOrderCoupons(cart);
      });
    });
  } else {
    getCart().then(function (cart) {
      applyOrderCoupons(cart);
    });
  }
})();
